import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';

type StatusType = 'normal' | 'low' | 'critical' | 'loading';

export function WebhookStatusIndicator() {
  const [status, setStatus] = useState<StatusType>('loading');
  const [twoHourCount, setTwoHourCount] = useState<number>(0);
  const [tenDayAverage, setTenDayAverage] = useState<number>(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch immediately on mount
    fetchWebhookStatus();
    
    // Then refresh every 5 minutes
    const interval = setInterval(fetchWebhookStatus, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchWebhookStatus = async () => {
    try {
      const now = new Date();
      const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
      const tenDaysAgo = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000);

      // Get count for last 2 hours
      const { count: recentCount, error: recentError } = await supabase
        .from('WebhookData')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', twoHoursAgo.toISOString());

      if (recentError) throw recentError;

      // Get historical data for 10-day average
      const { data: historicalData, error: historicalError } = await supabase
        .from('WebhookData')
        .select('created_at')
        .gte('created_at', tenDaysAgo.toISOString());

      if (historicalError) throw historicalError;

      // Calculate 2-hour period averages
      const twoHourPeriods = [];
      const totalPeriods = (10 * 24) / 2;

      for (let i = 0; i < totalPeriods; i++) {
        const periodStart = new Date(tenDaysAgo.getTime() + i * 2 * 60 * 60 * 1000);
        const periodEnd = new Date(periodStart.getTime() + 2 * 60 * 60 * 1000);
        
        const periodCount = historicalData?.filter(item => {
          const itemDate = new Date(item.created_at);
          return itemDate >= periodStart && itemDate < periodEnd;
        }).length || 0;
        
        twoHourPeriods.push(periodCount);
      }

      const average = twoHourPeriods.length > 0 
        ? twoHourPeriods.reduce((sum, count) => sum + count, 0) / twoHourPeriods.length 
        : 0;

      setTwoHourCount(recentCount || 0);
      setTenDayAverage(Math.round(average));

      // Determine status
      if (average <= 10) {
        // Not enough data for meaningful baseline
        setStatus('normal');
      } else if ((recentCount || 0) < average * 0.5) {
        setStatus('critical');
      } else if ((recentCount || 0) < average * 0.8) {
        setStatus('low');
      } else {
        setStatus('normal');
      }
    } catch (error) {
      console.error('Error fetching webhook status:', error);
      setStatus('normal'); // Default to normal if error
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'critical':
        return 'bg-red-500';
      case 'low':
        return 'bg-yellow-500';
      case 'normal':
        return 'bg-green-500';
      case 'loading':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'critical':
        return 'Critical';
      case 'low':
        return 'Low';
      case 'normal':
        return 'Normal';
      case 'loading':
        return 'Loading';
      default:
        return 'Unknown';
    }
  };

  const handleClick = () => {
    navigate('/webhook-data');
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={handleClick}
        className="flex min-h-11 min-w-11 items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 sm:gap-2 sm:px-3 sm:py-2 sm:text-sm"
        title="Click to view webhook monitoring details"
      >
        {/* Animated pulse for critical status */}
        <div className="relative flex items-center justify-center">
          <div 
            className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${getStatusColor()} ${
              status === 'critical' ? 'animate-pulse' : ''
            }`}
          />
          {status === 'critical' && (
            <div className="absolute h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-red-500 animate-ping opacity-75" />
          )}
        </div>
        
        <span className="hidden lg:inline">Webhook Status</span>
        <span className="hidden sm:inline lg:hidden">Webhooks</span>
        
        <span className={`font-bold ${
          status === 'critical' ? 'text-red-600' :
          status === 'low' ? 'text-yellow-600' :
          status === 'normal' ? 'text-green-600' :
          'text-gray-500'
        }`}>
          {getStatusText()}
        </span>

        {status === 'critical' && (
          <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-600" />
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && status !== 'loading' && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 w-64 pointer-events-none">
          <div className="space-y-1.5">
            <div className="flex justify-between">
              <span className="text-gray-300">Last 2 Hours:</span>
              <span className="font-semibold">{twoHourCount} webhooks</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">10-Day Average:</span>
              <span className="font-semibold">{tenDayAverage} webhooks</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-gray-700">
              <span className="text-gray-300">Status:</span>
              <span className={`font-semibold ${
                status === 'critical' ? 'text-red-400' :
                status === 'low' ? 'text-yellow-400' :
                'text-green-400'
              }`}>
                {getStatusText()}
                {tenDayAverage > 0 && ` (${Math.round((twoHourCount / tenDayAverage) * 100)}%)`}
              </span>
            </div>
          </div>
          <div className="mt-2 border-t border-gray-700 pt-2 text-xs text-gray-400">
            Click to view full monitoring dashboard
          </div>
          {/* Tooltip arrow */}
          <div className="absolute right-4 -top-1 w-2 h-2 bg-gray-900 transform rotate-45" />
        </div>
      )}
    </div>
  );
}







